import { useCallback, useEffect, useState } from 'react';
import { taxJurisdictionGateway } from './index';
import type { JurisdictionCatalog, JurisdictionEntry } from './types';

/** The catalog is reference data: it is read once per page load and shared by every screen that shows a jurisdiction. */
let cached: JurisdictionCatalog | null = null;
let inFlight: Promise<JurisdictionCatalog> | null = null;
let byCode = new Map<string, JurisdictionEntry>();

function load(): Promise<JurisdictionCatalog> {
  if (cached) return Promise.resolve(cached);
  inFlight ??= taxJurisdictionGateway
    .getCatalog()
    .then((catalog) => {
      cached = catalog;
      byCode = new Map(catalog.items.map((e) => [e.code, e]));
      return catalog;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/** Test helper: the cache is module-level, so a test that swaps the gateway must start from an empty one. */
export function resetJurisdictionCatalogForTests(): void {
  cached = null;
  inFlight = null;
  byCode = new Map();
}

export interface JurisdictionCatalogState {
  catalog: JurisdictionCatalog | null;
  /** The active entries, in the server's order (States, District, Territories). Empty until loaded. */
  items: JurisdictionEntry[];
  loading: boolean;
  failed: boolean;
  retry: () => void;
  /** The entry for a code, or undefined while loading or when the code is not in the catalog. */
  find: (code: string | null | undefined) => JurisdictionEntry | undefined;
  /** The entry whose name is exactly `name` (the employee screens receive a jurisdiction by name, not by code). */
  findByName: (name: string | null | undefined) => JurisdictionEntry | undefined;
}

/** The shared jurisdiction catalog (GET /api/tax-jurisdictions), loaded once. A failure is `failed` with a retry, never an exception. */
export function useJurisdictionCatalog(): JurisdictionCatalogState {
  const [catalog, setCatalog] = useState<JurisdictionCatalog | null>(cached);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (cached) return;
    let cancelled = false;
    load()
      .then((c) => {
        if (!cancelled) {
          setCatalog(c);
          setFailed(false);
        }
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const retry = useCallback(() => {
    setFailed(false);
    setAttempt((n) => n + 1);
  }, []);
  const find = useCallback((code: string | null | undefined) => (code ? byCode.get(code.toUpperCase()) : undefined), []);
  const findByName = useCallback((name: string | null | undefined) => (name ? [...byCode.values()].find((e) => e.name === name) : undefined), []);

  return {
    catalog,
    items: catalog ? catalog.items.filter((e) => e.isActive) : [],
    loading: catalog === null && !failed,
    failed,
    retry,
    find,
    findByName,
  };
}

/** The display name for a jurisdiction code ("Puerto Rico"), or the code itself while the catalog is loading or when the code is not in it. */
export function jurisdictionLabel(code: string | null | undefined): string {
  if (!code) return '';
  return byCode.get(code.toUpperCase())?.name ?? code;
}
